import { useEffect, useRef, type FC } from "react";
import { useApolloClient } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "@/shared/hooks";
import { usePlatform } from "@/shared/hooks/use-platform";
import { useUser } from "@/shared/hooks/use-user";
import type { MyAssistantConversationQueryType } from "@/types/graphql/site.generated";
import { MyAssistantConversationQuery } from "../api/graphql";
import { currentContentId, currentProductId } from "../lib/currentContentId";
import {
  closeAssistant,
  consumePendingStart,
  hydrate,
  useAssistantState,
} from "../model/assistantStore";
import {
  resetAssistantChat,
  sendAssistantTurn,
  type AssistantTurnInput,
} from "../model/sendAssistantTurn";
import { ASSISTANT_BOTTOM_OFFSET, ASSISTANT_Z_INDEX } from "./AssistantBubble";
import { AssistantComposer } from "./AssistantComposer";
import { AssistantMessageItem } from "./AssistantMessageItem";
import { AssistantSignIn } from "./AssistantSignIn";
import { AssistantAvatar } from "./AssistantAvatar";

const SUGGESTIONS = [
  "assistant.suggestPurchase",
  "assistant.suggestPlayback",
  "assistant.suggestRefund",
] as const;

interface AssistantPanelProps {
  name: string;
  avatarUrl: string | null;
}

/**
 * Жизненный цикл диалога: загрузка ленты вошедшему, стартовый ход из экрана
 * ошибки, сброс ленты при смене пользователя.
 */
function useAssistantSession() {
  const state = useAssistantState();
  const { user, refetch } = useUser();
  const { i18n } = useTranslation();
  const client = useApolloClient();
  const userId = user?.id ?? null;

  const send = async (
    input: Omit<AssistantTurnInput, "locale" | "pageUrl" | "contentId"> & {
      contentId?: string | null;
    },
  ) => {
    const { pathname } = window.location;
    const result = await sendAssistantTurn({
      ...input,
      locale: i18n.language,
      pageUrl: window.location.href,
      contentId:
        input.contentId !== undefined
          ? input.contentId
          : currentContentId(pathname),
      productId: input.productId ?? currentProductId(pathname),
    });
    // Сессия истекла: перечитываем пользователя, панель покажет вход.
    if (result === "unauthorized") refetch();
  };
  const sendRef = useRef(send);
  sendRef.current = send;

  // Первое открытие вошедшим: последняя лента с сервера. Загрузка
  // привязана к поколению ленты: после смены пользователя ответ для
  // прежнего отбрасывается, а загрузка для нового стартует, не дожидаясь его.
  const { generation } = state;
  const loadingRef = useRef<number | null>(null);
  useEffect(() => {
    if (!state.open || !userId || state.loaded || state.pendingStart) return;
    if (loadingRef.current === generation) return;
    loadingRef.current = generation;
    const load = async () => {
      try {
        const { data } = await client.query<MyAssistantConversationQueryType>({
          query: MyAssistantConversationQuery,
          fetchPolicy: "network-only",
        });
        hydrate(data?.myAssistantConversation ?? null, generation);
      } catch {
        hydrate(null, generation);
      } finally {
        if (loadingRef.current === generation) loadingRef.current = null;
      }
    };
    void load();
  }, [
    state.open,
    userId,
    state.loaded,
    state.pendingStart,
    generation,
    client,
  ]);

  // Панель открыта экраном ошибки: новый диалог с готовым вопросом, как
  // только зритель вошёл и прошлый ход закончился.
  useEffect(() => {
    if (!state.open || !userId || !state.pendingStart || state.sending) return;
    const start = consumePendingStart();
    if (!start) return;
    resetAssistantChat();
    void sendRef.current({
      text: start.prompt,
      origin: "ERROR",
      errorCode: start.errorCode,
      contentId: start.contentId,
      productId: start.productId,
      paymentId: start.paymentId,
      playerError: start.playerError,
    });
  }, [state.open, userId, state.pendingStart, state.sending]);

  return { state, send };
}

export const AssistantPanelContent: FC<AssistantPanelProps> = ({
  name,
  avatarUrl,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const { platform } = usePlatform();
  const { user, loading: userLoading, openSignIn } = useUser();
  const { state, send } = useAssistantSession();
  const listRef = useRef<HTMLDivElement>(null);

  const lastMessage = state.messages[state.messages.length - 1];
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [state.messages.length, lastMessage?.text, lastMessage?.kind]);

  // На телефоне панель на весь экран: внутренняя ссылка должна её убрать,
  // иначе открытое видео окажется под ней.
  const onInternalLinkClick = desktop ? undefined : closeAssistant;

  let body;
  if (!user) {
    body = userLoading ? (
      <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} />
      </Box>
    ) : (
      <AssistantSignIn onSignIn={openSignIn} />
    );
  } else {
    const empty = state.loaded && state.messages.length === 0;
    // «Помогло?» только под первым ответом диалога с кнопки ошибки.
    const helpfulMessageId =
      state.origin === "ERROR"
        ? (state.messages.find((m) => m.role === "assistant")?.id ?? null)
        : null;
    body = (
      <>
        <Box
          ref={listRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            overscrollBehavior: "contain",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {!state.loaded && (
            <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {empty && (
            <Box
              sx={{
                mt: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {SUGGESTIONS.map((key) => (
                <Button
                  key={key}
                  variant="outlined"
                  onClick={() => void send({ text: t(key) })}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  {t(key)}
                </Button>
              ))}
            </Box>
          )}
          {state.messages.map((message) => (
            <AssistantMessageItem
              key={message.id}
              message={message}
              assistantName={name}
              avatarUrl={avatarUrl}
              arenaTitle={platform?.title ?? ""}
              supportEmail={
                platform?.showSupportEmail
                  ? (platform.supportEmail ?? null)
                  : null
              }
              viewerEmail={user.email ?? null}
              onInternalLinkClick={onInternalLinkClick}
              askHelpful={message.id === helpfulMessageId}
            />
          ))}
        </Box>
        <AssistantComposer
          sending={state.sending}
          onSend={(text) => void send({ text })}
        />
      </>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.25,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <AssistantAvatar avatarUrl={avatarUrl} size={36} />
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ flex: 1, fontWeight: 600 }}
        >
          {name}
        </Typography>
        {user && (
          <Tooltip title={t("assistant.newChat")}>
            <span>
              <IconButton
                aria-label={t("assistant.newChat")}
                disabled={state.messages.length === 0}
                onClick={resetAssistantChat}
              >
                <AddCommentOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton
          aria-label={t("assistant.close")}
          edge="end"
          onClick={closeAssistant}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      {body}
    </Box>
  );
};

/**
 * Панель помощника: на sm и шире карточка 400x600 в правом нижнем углу, на
 * телефоне диалог на весь экран.
 */
export const AssistantPanel: FC<AssistantPanelProps> = (props) => {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const { open } = useAssistantState();

  if (!desktop) {
    return (
      <Dialog fullScreen open={open} onClose={closeAssistant}>
        <AssistantPanelContent {...props} />
      </Dialog>
    );
  }

  return (
    <Grow in={open} style={{ transformOrigin: "bottom right" }} unmountOnExit>
      <Paper
        elevation={12}
        role="dialog"
        aria-label={props.name}
        sx={{
          position: "fixed",
          right: 16,
          bottom: ASSISTANT_BOTTOM_OFFSET,
          zIndex: ASSISTANT_Z_INDEX,
          width: 400,
          height: 600,
          maxHeight: `calc(100vh - 32px - var(--cookie-banner-height, 0px))`,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <AssistantPanelContent {...props} />
      </Paper>
    </Grow>
  );
};
